import { motion } from "framer-motion";
import { CreditCard, Bitcoin, DollarSign, MessageCircle, Monitor, Headphones, Wallet, Smartphone } from "lucide-react";
import { SplitHeading } from "./Reveal";

const BENTO_IMG = "https://images.unsplash.com/photo-1637947112681-17485e3a32ba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Cell = ({ className = "", children, i, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 }}
    whileHover={{ y: -4 }}
    data-testid={testId}
    className={`group relative overflow-hidden rounded-sm border border-white/10 bg-[#0f0f16] p-7 transition-colors duration-500 hover:border-[#a259ff]/40 ${className}`}
  >
    {children}
  </motion.div>
);

const PAY = [
  [CreditCard, "Card via Stripe"],
  [Smartphone, "Apple Pay (Apple Cash)"],
  [Wallet, "PayPal"],
  [Bitcoin, "Crypto (BTC / ETH / USDT)"],
  [DollarSign, "Cash App"],
  [MessageCircle, "Order now, pay later by contact"],
];

export const Bento = ({ maxRank, count }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36" data-testid="bento-section">
    <div className="mb-14 grid gap-8 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <SplitHeading eyebrow="01 — Why OGM" lines={["Load in rich.", "Leave no grind."]} className="text-6xl md:text-8xl" />
      </div>
      <p className="text-base text-slate-300 md:col-span-4 md:col-start-9 md:text-lg">
        Every OGM account is set up before you buy: money in the bank, rank boosted and the unlocks listed on the card.
      </p>
    </div>

    <div className="grid grid-cols-1 gap-4 md:auto-rows-[240px] md:grid-cols-12">
      <Cell i={0} testId="bento-feature" className="min-h-[420px] p-0 md:col-span-7 md:row-span-2 md:min-h-0">
        <img src={BENTO_IMG} alt="Neon-lit supercar" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07070b] via-[#07070b]/40 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#e9d5ff]">Pre-loaded</p>
          <h3 className="mt-3 font-display text-5xl uppercase leading-none text-white md:text-7xl">Money. Rank.<br />Rides.</h3>
        </div>
      </Cell>

      <Cell i={1} testId="bento-platforms" className="md:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-300">Platform</p>
        <div className="mt-6 flex items-end justify-between gap-4">
          <span className="font-display text-8xl leading-none text-white">PC</span>
          <div className="flex gap-3">
            {[Monitor].map((I, k) => (
              <span key={k} className="grid h-14 w-14 place-items-center rounded-full border border-white/15 text-violet-300 transition-colors group-hover:border-[#a259ff]/60">
                <I className="h-6 w-6" />
              </span>
            ))}
          </div>
        </div>
        <p className="mt-4 text-sm text-slate-300">Modded PC accounts only.</p>
      </Cell>

      <Cell i={2} testId="bento-payments" className="md:col-span-5">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-300">Pay your way</p>
        <ul className="mt-5 space-y-3">
          {PAY.map(([I, t]) => (
            <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
              <I className="h-4 w-4 text-[#ff3dc8]" /> {t}
            </li>
          ))}
        </ul>
      </Cell>

      <Cell i={3} testId="bento-rank" className="md:col-span-4">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-300">Top rank in stock</p>
        <p className="mt-4 font-mono text-6xl font-extrabold text-[#e9d5ff]">{maxRank || "—"}</p>
        <p className="mt-3 text-sm text-slate-300">{count} packages live right now.</p>
      </Cell>

      <Cell i={4} testId="bento-support" className="md:col-span-8">
        <div className="flex h-full flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-violet-300">Handled by a real person</p>
            <h3 className="mt-4 font-display text-5xl uppercase leading-none text-white md:text-6xl">Order. Pay.<br /><span className="text-stroke">We deliver.</span></h3>
          </div>
          <Headphones className="h-16 w-16 text-[#ff3dc8] transition-transform duration-500 group-hover:rotate-[-8deg]" />
        </div>
      </Cell>
    </div>
  </section>
);
